import type WaDataGrid from "@web.awesome.me/webawesome-pro/dist/components/data-grid/data-grid.js";
import type WaDialog from "@web.awesome.me/webawesome-pro/dist/components/dialog/dialog.js";
import type WaPage from "@web.awesome.me/webawesome-pro/dist/components/page/page.js";
import htmx from "htmx.org";
import { html } from "lit";
import { checkNotNil } from "../../check-not-nil.js";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper.js";
import { stateProperty } from "../../property.js";
import { Metrics } from "../../schemas/metrics.js";
import type { ParserSchema, ParserType } from "../../schemas/parser.js";
import { gridSearch } from "../../search.js";
import { WmElement } from "../element.js";

type Items = ParserType<typeof Items>;
const Items = stateProperty(Metrics.schema);

export class WmMetricsGrid extends WmElement {
  static readonly TAG_NAME = "wm-metrics-grid";

  static properties = {
    items: Items,
    isMobile: { attribute: false },
  };

  declare items: Items;
  declare isMobile: boolean;
  #pageObserver?: MutationObserver;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Metrics>>;

  constructor() {
    super();
    this.items = [];
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Metrics.schema,
      callback: (v) => (this.items = v),
      logger: this.logger,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializePageView();
  }

  disconnectedCallback() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
    super.disconnectedCallback();
  }

  protected render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      ${this.#renderGrid()}`;
  }

  #renderGrid() {
    return html`
      <wa-dialog label="Are you sure?">
        This action is irreversible.
        <div slot="footer" class="wa-cluster">
          <wa-button variant="brand" data-dialog="close">Cancel</wa-button>
          <wa-button variant="danger" @click=${this.#confirmDeletion}>Confirm</wa-button>
        </div>
      </wa-dialog>
      <wa-data-grid
        label="Notes"
        paginate
        page-size="10"
        resizable
        row-key="id"
        size="s"
        striped
        with-search
      >
        <span slot="empty">No metrics yet, son.</span>
      </wa-data-grid>
      `;
  }

  protected updated(): void {
    this.#handleUpdated();
  }

  async #handleUpdated() {
    const dataGrid = this.#queryGrid();
    await dataGrid.updateComplete;
    dataGrid.searchFn = gridSearch;
    dataGrid.columns = [
      {
        field: "name",
        label: "Name",
        formatter: (v, row) =>
          html`<a href="/app/metrics/${row.id}" @click=${this.#nameClickHandler(row.id as string)}>${v}</a>`,
      },
      {
        field: "definition.kind",
        label: "Kind",
      },
      {
        field: "definition.defaultUnit",
        label: "Unit",
      },
      {
        field: "tags",
        label: "Tags",
        hidden: this.isMobile,
        formatter: (v) => html`<wm-tag-cell .tags=${v}></wm-tag-cell>`,
      },
      {
        id: "actions",
        label: "Actions",
        hidden: this.isMobile,
        // formatter: (_, row) => html`
        //   <wa-button size="xs" appearance="plain" @click=${() => htmx.ajax("GET", `/app/notes/${row.id}`, { target: "body", push: "true" })}>
        //     <wa-icon name="pen-to-square"></wa-icon>
        //   </wa-button>
        //   <wm-delete-button size="xs" as-icon @click=${() => this.#promptDeletionConfirmation(row.id as string)}>
        //   </wm-delete-button>
        // `,
        formatter: (_, row) => html`
          <wm-delete-button size="xs" as-icon @click=${() => this.#promptDeletionConfirmation(row.id as string)}>
          </wm-delete-button>
        `,
      },
    ];
    // dataGrid.rowDetail = (row) => html`
    //   <wm-note-details .details=${row} @wm-deletion-request=${(e: WmDeletionRequest) => this.#promptDeletionConfirmation(e.id)}></wm-note-details>
    // `;
    dataGrid.data = this.items ?? [];
  }

  #confirmDeletion() {
    const dialog = this.#queryDialog();
    const id = dialog.dataset.id;
    htmx.ajax("DELETE", `/app/metrics/${id}`);
  }

  #nameClickHandler(id: string) {
    return (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      htmx.ajax("GET", `/app/metrics/${id}`, {
        target: "body",
        push: "true",
      });
    };
  }

  #promptDeletionConfirmation(id: string) {
    const dialog = this.#queryDialog();
    dialog.dataset.id = id;
    dialog.open = true;
  }

  #initializePageView() {
    // At this time, we actually consider it an error if no wraping wa-page
    // can be found.
    const page = checkNotNil(document.querySelector("wa-page")) as WaPage;
    this.isMobile = page.view === "mobile";
    this.#pageObserver = new MutationObserver(() => {
      this.isMobile = page.view === "mobile";
    });
    this.#pageObserver.observe(page, {
      attributes: true,
      attributeFilter: ["view"],
    });
  }

  #queryDialog(): WaDialog {
    return checkNotNil(this.renderRoot.querySelector("wa-dialog"));
  }

  #queryGrid(): WaDataGrid {
    return this.queryNotNil("wa-data-grid");
  }
}

customElements.define(WmMetricsGrid.TAG_NAME, WmMetricsGrid);

declare global {
  interface HTMLElementTagNameMap {
    [WmMetricsGrid.TAG_NAME]: WmMetricsGrid;
  }
}
